import React from "react";
import RequestDemoForm from "../components/demo/RequestDemoForm";
import { Card, CardContent } from "@/components/ui/card";
import {
  CalendarClock,
  CreditCard,
  FileText,
  Clock,
  UserCheck,
  BadgeCheck,
} from "lucide-react";

const RequestDemoPage: React.FC = () => {
  return (
    <div className="min-h-screen bg-[#FAF7F2]">
      {/* Hero Section */}
      <div className="bg-secondary py-16 px-4">
        <div className="container mx-auto max-w-6xl">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-12 items-center">
            <div>
              <h1 className="text-4xl md:text-5xl font-extrabold text-gray-900 mb-4">
                Try <span className="text-primary">Hirelo</span> Today
              </h1>
              <p className="text-xl text-secondary-foreground/90 mb-6">
                The only HMRC recognised payroll software designed specifically
                for paying nannies. Experience our platform with pre-configured
                demo accounts.
              </p>
              <div className="flex flex-col sm:flex-row gap-4 mt-8">
                <div className="flex items-center gap-2 text-secondary-foreground">
                  <BadgeCheck className="h-6 w-6 text-white" />
                  <span>HMRC Approved</span>
                </div>
                <div className="flex items-center gap-2 text-secondary-foreground">
                  <UserCheck className="h-6 w-6 text-white" />
                  <span>Built for Nannies</span>
                </div>
                <div className="flex items-center gap-2 text-secondary-foreground">
                  <Clock className="h-6 w-6 text-white" />
                  <span>Setup in Minutes</span>
                </div>
              </div>
            </div>
            <div>
              <Card className="shadow-lg">
                <CardContent className="pt-6">
                  <RequestDemoForm />
                </CardContent>
              </Card>
            </div>
          </div>
        </div>
      </div>

      {/* Features Section */}
      <div className="py-16 px-4 bg-white">
        <div className="container mx-auto max-w-6xl">
          <h2 className="text-3xl font-bold text-center mb-12">
            Discover How Easy Nanny Payroll Can Be
          </h2>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            <FeatureCard
              icon={<CalendarClock className="h-8 w-8" />}
              title="Payroll in 30 Seconds"
              description="Our platform automates complex payroll calculations, letting you approve with just one click."
            />
            <FeatureCard
              icon={<CreditCard className="h-8 w-8" />}
              title="Automatic Tax Handling"
              description="Never worry about tax calculations again. We handle all HMRC submissions automatically."
            />
            <FeatureCard
              icon={<FileText className="h-8 w-8" />}
              title="Employment Contracts"
              description="Generate legally compliant contracts with our built-in contract tool created by experts."
            />
          </div>
        </div>
      </div>

      {/* Testimonials Section */}
      <div className="py-16 px-4 bg-[#FAF7F2]">
        <div className="container mx-auto max-w-6xl">
          <h2 className="text-3xl font-bold text-center mb-12">
            What Our Customers Say
          </h2>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            <TestimonialCard
              quote="I saved over £300 per year as Hirelo doesn't have hidden charges like some other services."
              author="Suk Gill"
              title="Parent of two"
            />
            <TestimonialCard
              quote="My payroll is now literally one click every month. It has saved me so much time and stress."
              author="Anneka Parmar"
              title="Parent to twin boys"
            />
            <TestimonialCard
              quote="The Hirelo portal allowed me to record my time off and check payslips instantly."
              author="Rosie Lee"
              title="Nanny in West London"
            />
          </div>
        </div>
      </div>

      {/* Final CTA */}
      <div className="py-12 px-4 bg-[#FAF7F2] text-center">
        <div className="container mx-auto max-w-3xl">
          <h2 className="text-3xl font-bold mb-4">Ready to Try Hirelo?</h2>
          <p className="text-lg mb-8">
            Fill out the form above to get instant access to our demo
            environment with sample employer and nanny accounts.
          </p>
        </div>
      </div>
    </div>
  );
};

// Feature Card Component
const FeatureCard: React.FC<{
  icon: React.ReactNode;
  title: string;
  description: string;
}> = ({ icon, title, description }) => {
  return (
    <div className="bg-white p-6 rounded-lg shadow-md border border-gray-100 hover:shadow-lg transition-shadow">
      <div className="text-primary mb-4">{icon}</div>
      <h3 className="text-xl font-bold mb-2">{title}</h3>
      <p className="text-gray-600">{description}</p>
    </div>
  );
};

// Testimonial Card Component
const TestimonialCard: React.FC<{
  quote: string;
  author: string;
  title: string;
}> = ({ quote, author, title }) => {
  return (
    <div className="bg-white p-6 rounded-lg shadow-md border border-gray-100">
      <div className="text-primary mb-4">
        <svg
          xmlns="http://www.w3.org/2000/svg"
          width="24"
          height="24"
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          strokeWidth="2"
          strokeLinecap="round"
          strokeLinejoin="round"
          className="lucide lucide-quote"
        >
          <path d="M3 21c3 0 7-1 7-8V5c0-1.25-.756-2.017-2-2H4c-1.25 0-2 .75-2 1.972V11c0 1.25.75 2 2 2 1 0 1 0 1 1v1c0 1-1 2-2 2s-1 .008-1 1.031V20c0 1 0 1 1 1z" />
          <path d="M15 21c3 0 7-1 7-8V5c0-1.25-.757-2.017-2-2h-4c-1.25 0-2 .75-2 1.972V11c0 1.25.75 2 2 2h.75c0 2.25.25 4-2.75 4v3c0 1 0 1 1 1z" />
        </svg>
      </div>
      <p className="text-gray-700 italic mb-4">{quote}</p>
      <div className="mt-4">
        <p className="font-bold">{author}</p>
        <p className="text-sm text-gray-500">{title}</p>
      </div>
    </div>
  );
};

export default RequestDemoPage;
